import type { ReactNode } from "react";
import { ActionBar, Button, Menu } from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import styles from "./DraftBar.module.css";

/**
 * What the season-registration draft puts in the lib `ActionBar`: what the
 * draft holds, what blocks it, and the submit — always on screen, whether
 * the roster is five rows or thirty-five. Replaces the button that used to
 * sit below the grid, where a failed submit changed nothing the leader
 * could see.
 *
 * `ActionBar` has no visibility rule of its own, which is what this bar
 * needs: unlike `BulkActionBar` it must stay mounted through its quiescent
 * state („Žádné rozpracované změny.").
 */

export interface DraftCounts {
  transfers: number;
  added: number;
  edited: number;
  removed: number;
}

/** One row that blocks the submit, named for the error list. */
export interface BlockingItem {
  key: string;
  label: string;
  message: string;
  /** Something to decide (a conflict, a removed or doubled person), not data missing. */
  decide?: boolean;
}

export interface DraftBarProps {
  counts: DraftCounts;
  /** Rows whose errors stop the submit; the chip opens them as a list. */
  blockingItems: BlockingItem[];
  dirty: boolean;
  ready: boolean;
  submitting: boolean;
  /**
   * Whether the club is already a member of this season.
   *
   * False makes the primary a different act: „Registrovat tým do sezóny",
   * pressable with nothing staged at all, because an empty submit means
   * „register the club, the soupiska comes later" (the server puts the
   * leader on it). True is the ordinary „Odeslat registraci", which needs
   * something to send.
   */
  clubRegistered: boolean;
  /** The season documents are not in yet (or failed to load): nothing can
   * be ticked, so nothing may be sent. */
  documentsUnready?: boolean;
  onSubmit: () => void;
  onDiscard: () => void;
  /** Scroll to (and flash) the row with the given key. */
  onJumpTo: (key: string) => void;
  /** A problem with saving the rozpracovaná registrace; none while it saves fine, or for a reader. */
  saveStatus?: ReactNode;
}

/**
 * People, not bookkeeping: „4 převedení, 1 nový, 2 odebraní" reads as a
 * roster change, where a total plus a breakdown said the same thing twice.
 * The adjectives agree with an implied „členové".
 */
function summary(c: DraftCounts): string {
  const parts: string[] = [];
  if (c.transfers > 0) {
    parts.push(
      `${c.transfers} ${pluralizeCzech(c.transfers, ["převedený", "převedení", "převedených"])}`,
    );
  }
  if (c.added > 0) {
    parts.push(
      `${c.added} ${pluralizeCzech(c.added, ["nový", "noví", "nových"])}`,
    );
  }
  if (c.edited > 0) {
    parts.push(
      `${c.edited} ${pluralizeCzech(c.edited, ["upravený", "upravení", "upravených"])}`,
    );
  }
  if (c.removed > 0) {
    parts.push(
      `${c.removed} ${pluralizeCzech(c.removed, ["odebraný", "odebraní", "odebraných"])}`,
    );
  }
  return parts.join(", ");
}

export function DraftBar({
  counts,
  blockingItems,
  dirty,
  ready,
  submitting,
  clubRegistered,
  documentsUnready = false,
  onSubmit,
  onDiscard,
  onJumpTo,
  saveStatus,
}: DraftBarProps) {
  const n = blockingItems.length;
  return (
    <ActionBar
      aria-label="Rozpracované změny"
      /* `start` is an aria-live="polite" region by default. Two reasons not
         to have one here: the warn chip is a lib `Menu`, and only `Modal`
         is portaled — the popover renders in place, i.e. inside the region,
         so opening it would get the whole error list read out as an update;
         and the summary changes on every keystroke in the grid. */
      announce={false}
      /* The status is two pieces (summary + warn chip), not a sentence. */
      startLayout="row"
      start={
        <>
          {dirty ? (
            <span className={styles.summary}>{summary(counts)}</span>
          ) : (
            <span className={styles.idle}>Žádné rozpracované změny.</span>
          )}
          {saveStatus && <span className={styles.saveStatus}>{saveStatus}</span>}
          {/* No aria-label on the Menu: the trigger text is self-describing,
            and an extra label would REPLACE it as the accessible name. */}
          {n > 0 && (
            <Menu
              triggerClassName={styles.errChip}
              trigger={
                <>
                  {blockingItems.some((b) => b.decide) ? "K dořešení" : "Chybí údaje"} u {n}{" "}
                  {pluralizeCzech(n, ["osoby", "osob", "osob"])}{" "}
                  <span aria-hidden>▾</span>
                </>
              }
              items={blockingItems.map((b) => ({
                id: b.key,
                label: (
                  <span className={styles.errItem}>
                    <strong>{b.label}</strong>
                    <span className={styles.errMsg}>{b.message}</span>
                  </span>
                ),
                onSelect: () => onJumpTo(b.key),
              }))}
            />
          )}
        </>
      }
    >
      <Button disabled={!dirty || submitting} onClick={onDiscard}>
        Zahodit změny
      </Button>
      <Button
        variant="primary"
        loading={submitting}
        /* An unregistered club may press this with an empty draft: joining
           the season IS the act, and it is the only way in. */
        disabled={
          clubRegistered
            ? !ready
            : submitting || blockingItems.length > 0 || documentsUnready
        }
        onClick={onSubmit}
      >
        {clubRegistered ? "Odeslat registraci" : "Registrovat tým do sezóny"}
      </Button>
    </ActionBar>
  );
}
